import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/format";
import { waNumberFor, waBusinessMessage } from "@/lib/wa";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import DocumentShareDialog from "@/components/documents/DocumentShareDialog";
import { Wallet, Share2 } from "lucide-react";

export default function Payments() {
  const { business } = useAuth();
  const [payments, setPayments] = useState([]);
  const [shareDoc, setShareDoc] = useState(null); // {id, label, waMessage} or null
  useEffect(() => { api.get("/payments").then((r) => setPayments(r.data)); }, []);
  const total = payments.reduce((s, p) => s + p.amount, 0);
  const openShare = async (p) => {
    let email = "", phone = "";
    if (p.customer_id) {
      try { const { data } = await api.get(`/customers/${p.customer_id}`); email = data?.email || ""; phone = waNumberFor(data); }
      catch { /* best-effort prefill only */ }
    }
    setShareDoc({
      id: p.id, customerId: p.customer_id || null, label: `Payment Receipt ${p.receipt_number}`, email, phone,
      waMessage: waBusinessMessage({
        business, kind: "payment", heading: "Payment Receipt",
        meta: [`Reference: ${p.receipt_number}`, `Date: ${fmtDate(p.created_at)}`],
        greeting: p.customer_name || "",
        body: [`Amount Received: *${money(p.amount)}*`, `Method: ${p.method}`, `Status: *Completed*`],
        closing: "Thank you.",
      }),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><Wallet className="w-7 h-7 text-amber-500" /> Customer Payments</h1>
        <p className="text-muted-foreground">All account payments and receipts. Original credit-sale history is always preserved.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">Payments Recorded</p><p className="mono text-2xl font-bold">{payments.length}</p></Card>
        <Card className="p-4"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Collected</p><p className="mono text-2xl font-bold text-emerald-500">{money(total)}</p></Card>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Receipt</TableHead><TableHead>Customer</TableHead><TableHead>Date</TableHead>
            <TableHead>Method</TableHead><TableHead>Applied To</TableHead><TableHead>By</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                <TableCell className="mono text-xs">{p.receipt_number}</TableCell>
                <TableCell className="font-medium">{p.customer_name}</TableCell>
                <TableCell className="text-xs">{fmtDate(p.created_at)}</TableCell>
                <TableCell><Badge variant="outline">{p.method}</Badge></TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.allocations?.map((a) => a.invoice_number).join(", ") || "General"}{p.unapplied > 0 && ` (+${money(p.unapplied)} credit)`}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.created_by}</TableCell>
                <TableCell className="text-right mono font-bold text-emerald-500">{money(p.amount)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-600" title="Digital Share"
                    onClick={() => openShare(p)} data-testid={`payment-share-${p.id}`}><Share2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {payments.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-10">No payments recorded yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <DocumentShareDialog open={!!shareDoc} onOpenChange={(o) => !o && setShareDoc(null)}
        title={shareDoc?.label || "Share Receipt"} docLabel={shareDoc?.label} waMessage={shareDoc?.waMessage}
        customerId={shareDoc?.customerId} customerEmail={shareDoc?.email} customerPhone={shareDoc?.phone}
        fileName={`${shareDoc?.label || "document"}.pdf`}
        getPdfBlob={() => api.get(`/payments/${shareDoc.id}/receipt.pdf`, { responseType: "blob" }).then((r) => r.data)}
        onEmail={(idem) => api.post(`/payments/${shareDoc.id}/receipt/email`, { idempotency_key: idem })}
        onWhatsAppShared={(method) => api.post(`/payments/${shareDoc.id}/receipt/whatsapp-shared`, { method }).catch(() => {})} />
    </div>
  );
}
